import React, { useState } from "react";
import "../styles/Landing.css";

// Using your existing Resolute logo URL from index.html
// (header logo src in the old hub shell)
const RESOLUTE_LOGO =
  "https://static.wixstatic.com/media/27ada95a9ed1785daf4a298093912264734ce9~mv2.png";

const isValidResoluteEmail = (email) => {
  const basicPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!basicPattern.test(email)) return false;
  const allowedDomains = ["resoluteink.co.uk", "resolute.ink"];
  const domain = email.split("@")[1]?.toLowerCase();
  return allowedDomains.includes(domain);
};

const Landing = ({ onAuthSuccess }) => {
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [step, setStep] = useState(1); // sign-up steps: 1 -> details, 2 -> password, 3 -> profile
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [profileImage, setProfileImage] = useState(null); // Data URL
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const resetErrors = () => setErrors({});

  const resetFormForMode = (nextMode) => {
    setMode(nextMode);
    resetErrors();
    setStep(1);
    setPassword("");
    setConfirm("");
  };

  const handleProfileFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setProfileImage(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    resetErrors();

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = name.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirm = confirm.trim();
    const newErrors = {};

    if (!trimmedEmail) {
      newErrors.email = "Email is required.";
    } else if (!isValidResoluteEmail(trimmedEmail)) {
      newErrors.email =
        "Use your resoluteink.co.uk or resolute.ink email address.";
    }

    if (mode === "signin") {
      if (!trimmedPassword) {
        newErrors.password = "Password is required.";
      }
      if (Object.keys(newErrors).length) {
        setErrors(newErrors);
        return;
      }
      completeAuth(trimmedEmail, trimmedPassword, trimmedName);
      return;
    }

    // SIGN-UP: validate based on step
    if (step === 1) {
      if (!trimmedName) {
        newErrors.name = "Full name is required.";
      }
      if (Object.keys(newErrors).length) {
        setErrors(newErrors);
        return;
      }
      setStep(2);
      return;
    }

    if (step === 2) {
      if (!trimmedPassword) {
        newErrors.password = "Password is required.";
      } else if (trimmedPassword.length < 8) {
        newErrors.password = "Password must be at least 8 characters.";
      }
      if (!trimmedConfirm) {
        newErrors.confirm = "Please confirm your password.";
      } else if (trimmedPassword !== trimmedConfirm) {
        newErrors.confirm = "Passwords do not match.";
      }
      if (Object.keys(newErrors).length) {
        setErrors(newErrors);
        return;
      }
      setStep(3);
      return;
    }

    if (step === 3) {
      // Profile photo is optional for now; you can enforce later if you like.
      completeAuth(trimmedEmail, trimmedPassword, trimmedName, profileImage);
    }
  };

  const completeAuth = (email, passwordValue, nameValue, avatarDataUrl) => {
    setSubmitting(true);

    const localPart = email.split("@")[0];
    const baseName =
      nameValue ||
      localPart
        .split(/[._-]/)
        .filter(Boolean)
        .map((s) => s[0].toUpperCase() + s.slice(1))
        .join(" ");
    const initials =
      baseName
        .split(" ")
        .filter(Boolean)
        .map((p) => p[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "--";

    const user = {
      email,
      name: baseName,
      role: "Staff",
      initials,
      avatar: avatarDataUrl || null,
    };

    // Same key as old vanilla app
    window.localStorage.setItem("hubUser", JSON.stringify(user));

    setTimeout(() => {
      setSubmitting(false);
      if (onAuthSuccess) onAuthSuccess(user);
    }, 400);
  };

  const renderSignupStep = () => {
    if (step === 1) {
      return (
        <>
          <div className="field">
            <label htmlFor="name">Full name</label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={errors.name ? "has-error" : ""}
              placeholder="Jane Doe"
            />
            {errors.name && <div className="field-error">{errors.name}</div>}
          </div>
          <div className="field">
            <label htmlFor="email">Work email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={errors.email ? "has-error" : ""}
              placeholder="name@resoluteink.co.uk"
            />
            {errors.email && <div className="field-error">{errors.email}</div>}
          </div>
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <div className="field">
            <label htmlFor="password">
              Set a password{" "}
              <span className="field-hint">(min 8 characters)</span>
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={errors.password ? "has-error" : ""}
              placeholder="Choose a password"
            />
            {errors.password && (
              <div className="field-error">{errors.password}</div>
            )}
          </div>
          <div className="field">
            <label htmlFor="confirm">Confirm password</label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={errors.confirm ? "has-error" : ""}
              placeholder="Re-enter your password"
            />
            {errors.confirm && (
              <div className="field-error">{errors.confirm}</div>
            )}
          </div>
        </>
      );
    }

    // step 3 – profile
    return (
      <>
        <div className="field">
          <label>Profile photo (optional)</label>
          <div className="profile-upload-row">
            <div className="profile-preview">
              {profileImage ? (
                <div
                  className="profile-preview-circle"
                  style={{
                    backgroundImage: `url(${profileImage})`,
                  }}
                />
              ) : (
                <div className="profile-preview-circle profile-preview-placeholder">
                  <i className="bi bi-person" />
                </div>
              )}
            </div>
            <div className="profile-upload-actions">
              <label className="btn-upload">
                <i className="bi bi-camera" />
                <span>Upload photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleProfileFile(e.target.files[0])}
                  hidden
                />
              </label>
              <p className="profile-upload-hint">
                This will be shown in your hub profile. You can change it later.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  };

  const isSignup = mode === "signup";

  return (
    <div className="landing-root">
      <div className="landing-card">
        <div className="landing-logo-row">
          <img
            src={RESOLUTE_LOGO}
            alt="Resolute"
            className="landing-logo-img"
          />
          <div className="landing-logo-text">
            <div className="landing-logo-title">Resolute Hub</div>
            <div className="landing-logo-sub">Staff tools & workflows</div>
          </div>
        </div>

        <div className="landing-tabs">
          <button
            type="button"
            className={!isSignup ? "tab active" : "tab"}
            onClick={() => resetFormForMode("signin")}
          >
            Sign in
          </button>
          <button
            type="button"
            className={isSignup ? "tab active" : "tab"}
            onClick={() => resetFormForMode("signup")}
          >
            Create account
          </button>
        </div>

        <h1 className="landing-heading">
          {mode === "signin"
            ? "Sign in to your Resolute hub"
            : "Create your Resolute account"}
        </h1>
        <p className="landing-copy">
          Use your Resolute work email to access approvals, internal news and
          the tools you use every day.
        </p>

        {mode === "signup" && (
          <div className="step-indicator">
            <span className={step === 1 ? "dot active" : "dot"} />
            <span className={step === 2 ? "dot active" : "dot"} />
            <span className={step === 3 ? "dot active" : "dot"} />
            <span className="step-label">
              {step === 1 && "Your details"}
              {step === 2 && "Password"}
              {step === 3 && "Profile photo"}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="landing-form">
          {mode === "signin" && (
            <>
              <div className="field">
                <label htmlFor="email">Work email</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? "has-error" : ""}
                  placeholder="name@resoluteink.co.uk"
                />
                {errors.email && (
                  <div className="field-error">{errors.email}</div>
                )}
              </div>
              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={errors.password ? "has-error" : ""}
                  placeholder="Enter your password"
                />
                {errors.password && (
                  <div className="field-error">{errors.password}</div>
                )}
              </div>
            </>
          )}

          {mode === "signup" && renderSignupStep()}

          <div className="landing-actions-row">
            {mode === "signup" && step > 1 && (
              <button
                type="button"
                className="landing-secondary"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
              >
                Back
              </button>
            )}

            <button
              type="submit"
              className="landing-submit"
              disabled={submitting}
            >
              {submitting
                ? "Please wait…"
                : mode === "signin"
                  ? "Sign in"
                  : step < 3
                    ? "Next"
                    : "Finish setup"}
            </button>
          </div>
        </form>

        <div className="landing-toggle-text">
          {mode === "signin" ? (
            <>
              New to the hub?{" "}
              <button
                type="button"
                className="link-button"
                onClick={() => resetFormForMode("signup")}
              >
                Create an account
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                type="button"
                className="link-button"
                onClick={() => resetFormForMode("signin")}
              >
                Sign in
              </button>
            </>
          )}
        </div>

        <div className="landing-footer">
          Internal staff access only. Your Resolute email is your identity.
        </div>
      </div>
    </div>
  );
};

export default Landing;
